import React, { useState, useEffect, useMemo } from 'react';
import {
    Card,
    CardHeader,
    CardContent,
    Button,
    Box,
    FormControlLabel,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Typography,
    LinearProgress
} from '@mui/material';
import { History } from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import axios from 'axios';
import Pagination from '../../components/common/Pagination';
import dateFormat from 'dateformat';

interface SchedulingLogsProps {
    refreshTrigger?: number;
}

export default function SchedulingLogs({ refreshTrigger }: SchedulingLogsProps) {
    const [logs, setLogs] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [total, setTotal] = useState(0);
    const [userOnly, setUserOnly] = useState(true);

    const [selectedResult, setSelectedResult] = useState<string | null>(null);

    const fetchLogs = () => {
        setLoading(true);
        // API expects 0-based page index
        axios.get(`/bank/web/scheduling/logs?page=${page - 1}&limit=${rowsPerPage}&userOnly=${userOnly}`)
            .then((res) => {
                setLogs(res.data.logs);
                setTotal(res.data.total);
            })
            .catch((err) => console.error(err))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchLogs();
    }, [page, rowsPerPage, userOnly, refreshTrigger]);

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    const handleRowsPerPageChange = (event: any) => {
        setRowsPerPage(event.target.value);
        setPage(1);
    };

    const columns: GridColDef[] = useMemo(() => [
        { 
            field: 'createdAt', 
            headerName: 'თარიღი', 
            width: 180, 
            valueFormatter: (params) => {
                if (!params.value) return '';
                return dateFormat(new Date(params.value as string), 'dd/mm/yyyy HH:MM:ss');
            }
        },
        { 
            field: 'user', 
            headerName: 'მომხმარებელი', 
            width: 200, 
            valueGetter: (params) => params.row.user?.name || 'სისტემა' 
        },
        { field: 'loan_id', headerName: 'სესხის ID', width: 150 },
        { 
            field: 'path', 
            headerName: 'ოპერაცია', 
            width: 250, 
            valueFormatter: (params) => {
                const p = params.value as string;
                if (p.includes('submit_schedule')) return 'გრაფიკის გაგზავნა';
                if (p.includes('submit_due_dates')) return 'ვადაგადაცილების გაგზავნა';
                if (p.includes('request_payment')) return 'გადახდის მოთხოვნა';
                if (p.includes('submit_payment')) return 'გადახდის გაგზავნა';
                return p;
            }
        },
        { 
            field: 'response_message', 
            headerName: 'შედეგი', 
            minWidth: 300,
            flex: 1,
            renderCell: (params: GridRenderCellParams) => (
                <div 
                    style={{ 
                        cursor: 'pointer', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        width: '100%',
                        color: '#1976d2'
                    }}
                    onClick={() => setSelectedResult(params.value as string)}
                >
                    {params.value}
                </div>
            )
        },
        { field: 'response_error', headerName: 'შეცდომა', width: 200 },
    ], []);

    return (
        <Card variant="outlined">
            <CardHeader 
                title="ისტორია" 
                titleTypographyProps={{ variant: 'h5', fontWeight: 600 }}
                avatar={<History />}
                action={
                    <Box display="flex" alignItems="center" gap={2}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={userOnly}
                                    onChange={(e) => setUserOnly(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label="მხოლოდ მომხმარებლის ოპერაციები"
                        />
                        <Button onClick={fetchLogs} size="small" variant="outlined">განახლება</Button>
                    </Box>
                }
            />
            <CardContent>
                <Box sx={{ height: 600, width: '100%' }}>
                    <DataGrid
                        rows={logs}
                        columns={columns}
                        getRowId={(row) => row._id}
                        loading={loading}
                        hideFooter
                        disableSelectionOnClick
                    />
                </Box>
                
                <Pagination
                    page={page}
                    count={Math.ceil(total / rowsPerPage)}
                    onChange={handlePageChange}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleRowsPerPageChange}
                />

                <Dialog
                    open={!!selectedResult}
                    onClose={() => setSelectedResult(null)}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle>შედეგი</DialogTitle>
                    <DialogContent>
                        <DialogContentText style={{ whiteSpace: 'pre-wrap' }}>
                            {selectedResult}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setSelectedResult(null)} color="primary">
                            დახურვა
                        </Button>
                    </DialogActions>
                </Dialog>
            </CardContent>
        </Card>
    );
}
